/**
 * Site Inspection appointments API.
 *
 * An on-site assessment performed before an installation. Lifecycle:
 * Scheduled -> Completed | Void. Carries the structured "Initial Site
 * Inspection Form" (form_state, keyed by field ids in
 * src/data/siteInspectionForm.ts) plus uploaded site photos.
 */

import { API_BASE } from '../config/apiBase';
import { AttachmentVoidFields, patchAttachmentVoid } from './attachmentVoid';

export type SiteInspectionStatus = 'Scheduled' | 'Completed' | 'Void';

export type SiteInspectionFormState = Record<string, unknown>;

export interface SiteInspectionAssignee {
  user_id: number;
  username: string;
}

export interface SiteInspectionRow {
  id: number;
  client_id: number | null;
  /** The linked client's Job Code / Deployment ID, e.g. A-000101. */
  job_code: string | null;
  order_number: string | null;
  business_name: string;
  business_address: string | null;
  point_of_contact: string | null;
  poc_email: string | null;
  poc_phone: string | null;
  inspection_at: string | null;
  /** Null for a single-day inspection; set when the inspection spans a date range. */
  inspection_end_at: string | null;
  status: SiteInspectionStatus;
  void_reason: string | null;
  form_state: SiteInspectionFormState;
  installation_appointment_id: number | null;
  assignees: SiteInspectionAssignee[];
  created_by_user_id: number | null;
  created_by_username: string | null;
  completed_at: string | null;
  voided_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface SiteInspectionListResponse {
  scheduled: SiteInspectionRow[];
  completed: SiteInspectionRow[];
  void: SiteInspectionRow[];
}

export interface SiteInspectionAttachmentRow extends AttachmentVoidFields {
  id: number;
  appointment_id: number;
  field_id: string;
  original_file_name: string;
  uploaded_by_user_id: number | null;
  uploaded_by: string | null;
  created_at: string;
}

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || fallback);
  return data as T;
}

export async function fetchSiteInspections(): Promise<SiteInspectionListResponse> {
  const res = await fetch(`${API_BASE}/api/site-inspection-appointments`);
  return jsonOrThrow(res, 'Failed to fetch inspections');
}

export async function fetchSiteInspection(id: number): Promise<SiteInspectionRow> {
  const res = await fetch(`${API_BASE}/api/site-inspection-appointments/${id}`);
  return jsonOrThrow(res, 'Failed to fetch inspection');
}

export interface CreateSiteInspectionPayload {
  user_id: number;
  client_id?: number | null;
  order_number?: string | null;
  business_name: string;
  business_address?: string | null;
  point_of_contact?: string | null;
  poc_email?: string | null;
  poc_phone?: string | null;
  inspection_at?: string | null;
  inspection_end_at?: string | null;
  assignee_user_ids?: number[];
  form_state?: SiteInspectionFormState;
  /** When true, email the client (poc_email) that their site inspection is scheduled. */
  send_client_email?: boolean;
}

export async function createSiteInspection(payload: CreateSiteInspectionPayload): Promise<SiteInspectionRow> {
  const res = await fetch(`${API_BASE}/api/site-inspection-appointments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to create inspection');
}

export interface UpdateSiteInspectionPayload {
  business_name?: string;
  business_address?: string | null;
  point_of_contact?: string | null;
  poc_email?: string | null;
  poc_phone?: string | null;
  inspection_at?: string | null;
  inspection_end_at?: string | null;
  form_state?: SiteInspectionFormState;
  status?: SiteInspectionStatus;
  void_reason?: string | null;
  installation_appointment_id?: number | null;
  assignee_user_ids?: number[];
}

export async function updateSiteInspection(id: number, payload: UpdateSiteInspectionPayload): Promise<SiteInspectionRow> {
  const res = await fetch(`${API_BASE}/api/site-inspection-appointments/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to update inspection');
}

export function completeSiteInspection(id: number): Promise<SiteInspectionRow> {
  return updateSiteInspection(id, { status: 'Completed' });
}

export function voidSiteInspection(id: number, reason: string): Promise<SiteInspectionRow> {
  return updateSiteInspection(id, { status: 'Void', void_reason: reason });
}

// ── Photos ──

export async function fetchSiteInspectionAttachments(id: number): Promise<SiteInspectionAttachmentRow[]> {
  const res = await fetch(`${API_BASE}/api/site-inspection-appointments/${id}/attachments`);
  const data = await jsonOrThrow<{ attachments: SiteInspectionAttachmentRow[] }>(res, 'Failed to load photos');
  return data.attachments || [];
}

export async function uploadSiteInspectionAttachment(
  id: number,
  fieldId: string,
  file: File,
  userId?: number | null
): Promise<SiteInspectionAttachmentRow> {
  const form = new FormData();
  form.append('file', file);
  form.append('field_id', fieldId);
  if (userId != null && userId > 0) form.append('user_id', String(userId));
  const res = await fetch(`${API_BASE}/api/site-inspection-appointments/${id}/attachments`, {
    method: 'POST',
    body: form,
  });
  return jsonOrThrow(res, 'Failed to upload photo');
}

export function siteInspectionAttachmentDownloadUrl(id: number, attachmentId: number): string {
  return `${API_BASE}/api/site-inspection-appointments/${id}/attachments/${attachmentId}/download`;
}

/** Sets a photo aside with a reason. The photo stays on the inspection and can be restored. */
export function voidSiteInspectionAttachment(
  id: number,
  attachmentId: number,
  reason: string,
  userId?: number | null
): Promise<SiteInspectionAttachmentRow> {
  return patchAttachmentVoid(
    `${API_BASE}/api/site-inspection-appointments/${id}/attachments/${attachmentId}`,
    reason,
    userId
  );
}

export function unvoidSiteInspectionAttachment(
  id: number,
  attachmentId: number,
  userId?: number | null
): Promise<SiteInspectionAttachmentRow> {
  return patchAttachmentVoid(
    `${API_BASE}/api/site-inspection-appointments/${id}/attachments/${attachmentId}`,
    null,
    userId
  );
}
